import { Component, Input, OnInit, ElementRef, ViewChild, SimpleChanges } from "@angular/core";
import { environment } from "@env/environment";
import { GameSettingsService } from "../game-settings.service";
import domtoimage from 'dom-to-image-more';

@Component({
  selector: "qr-code-preview",
  templateUrl: "./qr-code-preview.component.html",
  styleUrls: ["./qr-code-preview.component.scss"],
})
export class QrCodePreviewComponent implements OnInit {
  @ViewChild("captureArea") captureArea: ElementRef;
  s3BucketUrl = environment.s3BucketUrl;
  @Input("gameMenuArt") gameMenuArt: string | null = null;
  @Input("color1") color1: string | null = "#efeeee";
  @Input("color2") color2: string | null = "#efeeee";
  @Input("autoPrizes") autoPrizes: any | null = null;
  @Input("gameId") gameId: string | null = null;
  @Input("action") action: string | null = null;
  @Input('winPattern') winPattern: any | null = null

  prizesTransformed: any[] = [];

  showPrizes = true;
  qrTitle = "SCAN ME";

  constructor(public gameSettingsService: GameSettingsService) {}

  ngOnInit() { }
  
  ngOnChanges(changes: SimpleChanges) {
    const hasValidWinPattern = Array.isArray(this.winPattern) && this.winPattern.length > 0;
    const hasValidAutoPrizes = Array.isArray(this.autoPrizes) && this.autoPrizes.length > 0;
  
    if ((hasValidWinPattern || hasValidAutoPrizes)) {
      this.prizesTransformed = this.transformPrizes();
    }
  }
  

  trackById(index: number, item: any): string | number {
    return item.id || index;
  }

  transformPrizes() {
    const prizes = [];
  
    const winPattern = (this.winPattern && this.winPattern.length > 0)
      ? this.winPattern
      : (this.gameSettingsService.winPattern || []);
  
    const autoPrizes = (this.autoPrizes && this.autoPrizes.length > 0)
      ? this.autoPrizes
      : (this.gameSettingsService.autoSelectPrizes || []);
  
    // Collect winPattern prizes
    for (let i = 0; i < winPattern.length; i++) {
      const wP = winPattern[i];
      if (wP && wP.cashPrizeValue !== "") {
        prizes.push({
          data: wP.image1,
          index: wP.index,
          type: "exact-match",
        });
      }
    }
  
    // Collect autoPrizes
    for (let j = 0; j < autoPrizes.length; j++) {
      const aP = autoPrizes[j];
      if (aP && aP.title && aP.title !== "") {
        prizes.push({
          data: aP.image1,
          index: aP.position,
          type: "auto-select-prize",
        });
      }
    }
  
    // Sort prizes by index
    prizes.sort(function(a, b) {
      return a.index - b.index;
    });

    // Limit to max 8 prizes (2 rows of 4)
    return prizes.slice(0, 8);
  }
  

  async downloadScreenshot() {
    // Preload all images first (optional, dom-to-image handles most cases)
    // You can keep this for reliability, but dom-to-image will try to load images itself
    const imageUrls = this.transformPrizes().map(
      (p) => this.getQrPrizeImage(p)
    );
    if (this.gameMenuArt) imageUrls.push(this.s3BucketUrl + this.gameMenuArt);

    await this.preloadImages(imageUrls);

    // Then proceed with capture
    this.captureScreenshotDomToImage();
  }

  getQrPrizeImage(prizeWrapper: any): string {
    if (!prizeWrapper || !prizeWrapper.data) return '/assets/images/image-not-found-150x150.png';
    
    const image1 = prizeWrapper.data;

    // Check Embed
    if (image1.type === 'embed') {
        const embedCode = image1.image || "";
        const match = embedCode.match(/embed\/([a-zA-Z0-9_-]+)/);
        if (match && match[1]) {
            return `https://img.youtube.com/vi/${match[1]}/0.jpg`;
        }
        return '/assets/images/image-not-found-150x150.png';
    }

    // Normal Image
    if (image1.image) {
        if (image1.image.startsWith('http') || image1.image.startsWith('data:')) return image1.image;
        return this.s3BucketUrl + image1.image;
    }
    
    // Exact match fallback
    if (prizeWrapper.type === 'exact-match') {
         return '/assets/images/cash prize.png';
    }

    return '/assets/images/image-not-found-150x150.png';
  }

  private preloadImages(urls: string[]): Promise<void[]> {
    return Promise.all(
      urls.map((url) => {
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.src = url;
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Continue even if some fail
        });
      })
    );
  }

  private captureScreenshotDomToImage() {
    const element = this.captureArea.nativeElement;

    // Hide interactive elements before capture
    const interactiveElements = element.querySelectorAll(
      "button, mat-slide-toggle, mat-form-field"
    );
    interactiveElements.forEach((el) => (el.style.visibility = "hidden"));

    // Add a small delay to ensure DOM updates are rendered
    setTimeout(() => {
      domtoimage.toPng(element, {
        cacheBust: true,
        bgcolor: '#fff',
        style: {
          transform: '',
          boxShadow: 'none',
        },
        filter: (node) => {
          // Ignore interactive elements
          if (
            node.tagName === "BUTTON" ||
            (node.classList && node.classList.contains("mat-slide-toggle")) ||
            (node.classList && node.classList.contains("mat-form-field"))
          ) {
            return false;
          }
          return true;
        },
      })
        .then((dataUrl: string) => {
          // Restore interactive elements
          interactiveElements.forEach(
            (el) => (el.style.visibility = "visible")
          );

          // Create and trigger download
          const link = document.createElement("a");
          link.download = `game-card-${new Date().getTime()}.png`;
          link.href = dataUrl;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err: any) => {
          console.error("dom-to-image capture error:", err);
          interactiveElements.forEach(
            (el) => (el.style.visibility = "visible")
          );
          // Optionally, fallback to print or show error
        });
    }, 100);
  }

  // Fallback: Show error or use print dialog
  private fallbackCapture() {
    alert('Screenshot failed. Please check CORS settings for S3 images or try using the browser print dialog (Ctrl+P).');
  }

  getPrizeStyle(prizeCount: number): { width: string; height: string } {
    let size = 56;
  
    if (prizeCount === 1) {
      size = 80;
    } else if (prizeCount === 2) {
      size = 70;
    } else if (prizeCount === 3) {
      size = 60;
    } else if (prizeCount >= 4) {
      size = 56;
    }
  
    return {
      width: `${size}px`,
      height: `${size}px`
    };
  }
  
}
